// react
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// components
import {
  GenericFlowProps,
  OnboardingActions,
  OnboardingActionType,
  toGenericOnboarding,
} from '@/components/pages/OnboardingFlow/OnboardingFlow';
import { Card, SpeechConfigItem } from '@/components/atoms';

// assets
import { css } from 'styled-components';

/**
 * OnboardingRadio renders the Radio screen for onboarding flows.
 */
export const OnboardingRadio = toGenericOnboarding(
  ({
    config,
    currentIndex = 0,
    handleClick,
  }: GenericFlowProps<OnboardingRadioConfigItem>) => {
    return (
      <Background>
        <StyledOnboardingRadio>
          <Wrapper>
            {config[currentIndex]?.title && (
              <Title>{config[currentIndex]?.title}</Title>
            )}
            {config[currentIndex]?.subTitle && (
              <SubTitle>{config[currentIndex]?.subTitle}</SubTitle>
            )}
          </Wrapper>

          <Content>
            {config[currentIndex].options && (
              <RadioWrapper>
                {config[currentIndex].options?.map((option) => (
                  <Radio
                    key={option.to}
                    to={option.to}
                    onClick={() => {
                      handleClick?.(OnboardingActionType.End);
                    }}
                  >
                    <Card elevation={0}>
                      <ItemWrapper>
                        <TextWrapper>{option.text}</TextWrapper>
                        {option.subText && (
                          <SubTextWrapper>{option.subText}</SubTextWrapper>
                        )}
                      </ItemWrapper>
                    </Card>
                  </Radio>
                ))}
              </RadioWrapper>
            )}
          </Content>
        </StyledOnboardingRadio>
      </Background>
    );
  },
);

const StyledOnboardingRadio = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  gap: ${(props) => props.theme.spacing[32]};

  height: 100%;
`;

const Background = styled.div`
  height: 100%;
`;

const Content = styled.div`
  width: 100%;
  min-height: 0;
  overflow: hidden;
  padding: ${(props) => props.theme.spacing[12]};
  overflow-y: auto;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: ${(props) => props.theme.spacing[32]};
`;

const Title = styled.div`
  font: ${(props) => props.theme.typography.heading['6xl']};
  color: ${(props) => props.theme.color.primary[80]};
  min-height: 60px;
  text-align: left;
  width: 100%;
  ${(props) =>
    props.theme.breakpoint.md(css`
      min-height: none;
      font: ${props.theme.typography.heading['6xl']};
    `)};
`;

const SubTitle = styled.div`
  font: ${(props) => props.theme.typography.heading['2xl']};
  color: ${(props) => props.theme.color.neutral[90]};
  margin-bottom: ${(props) => props.theme.spacing[8]};
  text-align: left;
  width: 100%;
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  margin-bottom: ${(props) => props.theme.spacing[48]};
  overflow: visible;

  align-items: center;
  gap: ${(props) => props.theme.spacing[16]};
`;

const Radio = styled(Link)`
  text-decoration: none;
  width: 100%;
  color: ${(props) => props.theme.color.neutral[90]};
  font: ${(props) => props.theme.typography.body.sm.medium};
  box-sizing: border-box;
  cursor: pointer;
  padding: 0;

  ${(props) =>
    props.theme.breakpoint.sm(css`
      font: ${props.theme.typography.body.md.medium};
    `)}

  transition-duration: 0.2s;
  &:hover {
    transform: scale(1.02);
  }
`;

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[4]};
`;

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SubTextWrapper = styled.div`
  display: flex;
  align-items: center;
  font: ${(props) => props.theme.typography.body.sm.medium};
  color: ${(props) => props.theme.color.neutral[70]};
`;

export interface RadioOption {
  to: string;
  text: string;
  subText?: string;
}

export interface OnboardingRadioConfigItem extends OnboardingActions {
  id: string;
  title: string;
  subTitle: string;
  keniSpeech?: SpeechConfigItem[];
  buttonText?: string;
  options?: RadioOption[];
  keniWidth?: string;
}

OnboardingRadio.displayName = 'OnboardingRadio';
export default toGenericOnboarding(OnboardingRadio);
